"use client";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

interface MercadoItem {
  id: string;
  mercado?: string;
  titulo: string;
  intro?: string;
  excel_url?: string | null;
  name_excel?: string | null;
}
interface Market { id: number; name: string };

export default function Mercados(): JSX.Element {
  const API = process.env.SAE_MERCADOS_API_URL;
  const [showForm, setShowForm] = useState(false);
  const [items, setItems] = useState<MercadoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [markets, setMarkets] = useState<Market[]>([]);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [marketsError, setMarketsError] = useState<string|null>(null);

  // Form (simple)
  const [formMarketId, setFormMarketId] = useState<number | ''>('');
  const [formTitulo, setFormTitulo] = useState("");
  const [formIntro, setFormIntro] = useState("");
  const [formFile, setFormFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // === Listar ===

  const loadMarkets = async () => {
    setMarketsLoading(true);
    setMarketsError(null);
    try {
      const r = await fetch("https://app-sae-api.vercel.app/api/sae-mercados/markets");
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setMarkets(data.markets || []);
    } catch (e) {
      setMarketsError(e instanceof Error ? e.message : "Error desconocido");
    } finally {
      setMarketsLoading(false);
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://app-sae-api.vercel.app/api/sae-mercados/content?includeUrl=1");
      const data = await res.json();
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void loadItems(); void loadMarkets(); }, []);

  // === Borrar ===
  const handleDelete = async (id: string) => {
    if (!confirm("¿Seguro que deseas borrar esta publicación?")) return;
    try {
      const res = await fetch(`https://app-sae-api.vercel.app/api/sae-mercados/content/${id}/delete`, { method: "POST" });
      console.log(res);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
    } catch {
      alert("Error al borrar");
    }
  };

  // === Crear (subir archivo y luego crear registro) ===
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formMarketId  || !formTitulo) {
      alert("Mercado y Título son obligatorios");
      return;
    }
    if (!formFile) {
      alert("Sube un archivo Excel");
      return;
    }

    setSubmitting(true);
    try {
      // 1) pedir URL firmada -> devuelve { key, file_name, signedUrl }
      const r1 = await fetch(`https://app-sae-api.vercel.app/api/sae-mercados/content/upload-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename: formFile.name }),
      });
      console.log(r1);
      if (!r1.ok) throw new Error("No se pudo generar URL de subida");
      const { key, fileName, signedUrl } = await r1.json();

      // 2) subir el archivo
      const up = await fetch(signedUrl, {
        method: "PUT",
        headers: { "Content-Type": formFile.type || "application/octet-stream" },
        body: formFile,
      });
      if (!up.ok) throw new Error("Falló la subida del archivo");

      const marketName = markets.find(m => m.id === formMarketId)?.name || '';

      // 3) crear registro -> envia id_excel y name_excel
      const r2 = await fetch(`https://app-sae-api.vercel.app/api/sae-mercados/content`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mercado: marketName,
          titulo: formTitulo,
          intro: formIntro,
          id_excel: key,      // ruta en bucket (uuid/NOMBRE.xlsx)
          name_excel: fileName, // nombre visible
        }),
      });
      if (!r2.ok) throw new Error("Falló la creación");
      const { item } = await r2.json();

      setItems((prev) => [item, ...prev]);

      // limpiar form
      setFormMarketId('');
      setFormTitulo("");
      setFormIntro("");
      setFormFile(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Error al crear");
    } finally {
      setSubmitting(false);
    }
    await loadItems();
  };

  return (
    <Container>
      <TitleContainer>
        <Title>SAE Mercados</Title>
      </TitleContainer>

      {/* Formulario simple */}
    {showForm && (
      <form onSubmit={handleSubmit} style={{ marginBottom: 24, maxWidth: 560 }}>
        <div style={{ marginBottom: 8 }}>
        <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>
          Mercado *
        </label>
        <select
          value={formMarketId}
          onChange={(e) => {setFormMarketId(e.target.value ? Number(e.target.value) : '')}}
          style={{ width: "100%", padding: 10, border: "1px solid #e5e7eb", borderRadius: 8 }}
        >
          <option value="">Selecciona un mercado…</option>
          {markets.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        {marketsLoading && <small>Cargando mercados…</small>}
        {marketsError && <small style={{color:'red'}}>Error: {marketsError}</small>}
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>
            Título *
          </label>
          <input
            value={formTitulo}
            onChange={(e) => {setFormTitulo(e.target.value)}}
            placeholder="Ej: Reporte semanal"
            style={{ width: "100%", padding: 10, border: "1px solid #e5e7eb", borderRadius: 8 }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>
            Intro
          </label>
          <textarea
            value={formIntro}
            onChange={(e) => {setFormIntro(e.target.value)}}
            rows={3}
            placeholder="Breve descripción…"
            style={{ width: "100%", padding: 10, border: "1px solid #e5e7eb", borderRadius: 8 }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>
            Subir Excel *
          </label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {setFormFile(e.target.files?.[0] || null)}}
            style={{ width: "100%" }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            background: "#1e40af",
            color: "white",
            padding: "10px 14px",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          {submitting ? "Guardando…" : "Guardar"}
        </button>
      </form>
      )}
      <button
        onClick={() => {setShowForm(v => !v)}}
        style={{
            background: "#1e40af",
            color: "white",
            padding: "8px 12px",
            border: "none",
            borderRadius: 8,
            fontSize: 10,
            cursor: "pointer",
          }}
      >
        {showForm ? "Cancelar" : "Agregar publicación"}
      </button>

      {loading && <p>Cargando…</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}

      {!loading && !error && (
        <>
          {items.length === 0 ? (
            <p>No hay publicaciones disponibles.</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Artículo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <strong>{m.titulo}</strong>
                      {m.mercado && (
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          Mercado: <code>{m.mercado}</code>
                        </div>
                      )}
                      {m.intro && <p>{m.intro}</p>}
                    </td>
                    <td>
                        {m.excel_url && (
                        <>
                            <a href={m.excel_url} target="_blank" rel="noreferrer" download={m.name_excel || undefined}>
                            Descargar
                            </a>
                            {" | "}
                        </>
                        )}
                      <span
                        style={{ color: "red", cursor: "pointer" }}
                        onClick={() => { void handleDelete(m.id); }}
                      >
                        Borrar
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Container>
  );
}

/* ====== estilos ====== */
const Container = styled.section`
  padding: 24px;
  margin-left: 192px;
  margin-top: 72px;
  width: calc(100vw - 192px);

  @media screen and (max-width: 860px) {
    margin-left: 0px;
    width: 100vw;
    margin-top: 70px;
    padding: 16px;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.p`
  font-size: 28px;
  font-family: ${(props) => props.theme.fonts.semiBold};
  color: ${(props) => props.theme.colors.primary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 96px;

  th {
    text-align: left;
    padding: 12px;
    border-bottom: 2px solid #e5e7eb;
  }

  td {
    padding: 12px;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: top;
  }

  tr:hover {
    background: #f9fafb;
  }
`;
